import { useState, useCallback } from 'react'

export const MODAL_TYPES = {
  CHANGE_EMAIL: 'change-email',
  CHANGE_PASSWORD: 'change-password',
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
  NO_USER: 'no-user',
} as const

export type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES]

interface PasswordChangeState {
  isPasswordChangeModalOpen: boolean
  isPasswordResetModalOpen: boolean
  isConfirmModalOpen: boolean
  isErrorModalOpen: boolean
  newEmail: string | null
  errorType: ModalType | null
  confirmationType: ModalType | null
}

interface PasswordChangeActions {
  openPasswordChangeModal: () => void
  closePasswordChangeModal: () => void
  openPasswordResetModal: () => void
  closePasswordResetModal: () => void
  closeConfirmModal: () => void
  openErrorModal: (errorType: ModalType) => void
  closeErrorModal: () => void
  handlePasswordChangeSuccess: () => void
  handlePasswordResetSuccess: () => void
  handleForgotPasswordSuccess: (email: string) => void
  resetFlow: () => void
}

type UsePasswordChangeFlow = PasswordChangeState & PasswordChangeActions

export const usePasswordChange = (): UsePasswordChangeFlow => {
  const [state, setState] = useState<PasswordChangeState>({
    isPasswordChangeModalOpen: false,
    isPasswordResetModalOpen: false,
    isConfirmModalOpen: false,
    isErrorModalOpen: false,
    newEmail: null,
    errorType: null,
    confirmationType: null,
  })

  const openPasswordChangeModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isPasswordChangeModalOpen: true,
    }))
  }, [])

  const closePasswordChangeModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isPasswordChangeModalOpen: false,
    }))
  }, [])

  const openPasswordResetModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isPasswordResetModalOpen: true,
    }))
  }, [])

  const closePasswordResetModal = useCallback(
    /* istanbul ignore next */ () => {
      setState(prev => ({
        ...prev,
        isPasswordResetModalOpen: false,
      }))
    },
    [],
  )

  const closeConfirmModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isConfirmModalOpen: false,
    }))
  }, [])

  const openErrorModal = useCallback((type: ModalType) => {
    setState(prev => ({
      ...prev,
      isPasswordChangeModalOpen: false,
      isPasswordResetModalOpen: false,
      isErrorModalOpen: true,
      errorType: type,
    }))
  }, [])

  const closeErrorModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isErrorModalOpen: false,
      errorType: null,
    }))
  }, [])

  const handlePasswordChangeSuccess = useCallback(() => {
    setState(prev => ({
      ...prev,
      isPasswordChangeModalOpen: false,
      isConfirmModalOpen: true,
      confirmationType: MODAL_TYPES.CHANGE_PASSWORD,
    }))
  }, [])

  const handlePasswordResetSuccess = useCallback(() => {
    setState(prev => ({
      ...prev,
      isPasswordResetModalOpen: false,
      isConfirmModalOpen: true,
      confirmationType: MODAL_TYPES.RESET_PASSWORD,
    }))
  }, [])

  const handleForgotPasswordSuccess = useCallback((email: string) => {
    setState(prev => ({
      ...prev,
      isConfirmModalOpen: true,
      newEmail: email,
      confirmationType: MODAL_TYPES.FORGOT_PASSWORD,
    }))
  }, [])

  const resetFlow = useCallback(
    /* istanbul ignore next */ () => {
      setState({
        isPasswordChangeModalOpen: false,
        isPasswordResetModalOpen: false,
        isConfirmModalOpen: false,
        isErrorModalOpen: false,
        newEmail: null,
        errorType: null,
        confirmationType: null,
      })
    },
    [],
  )

  return {
    ...state,
    openPasswordChangeModal,
    closePasswordChangeModal,
    openPasswordResetModal,
    closePasswordResetModal,
    closeConfirmModal,
    openErrorModal,
    closeErrorModal,
    handlePasswordChangeSuccess,
    handlePasswordResetSuccess,
    handleForgotPasswordSuccess,
    resetFlow,
  }
}
